import React, { useEffect, useRef, useState } from "react";
import "./Crossword.css";
import { Helmet } from "react-helmet";
import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";

const numRows = 10;  // 10 rows
const numCols = 12;  // 12 columns

// Function to create an empty crossword grid
const generateEmptyGrid = () => {
    return Array.from({ length: numCols }, () => Array(numRows).fill(""));
};

const generatePossibleGrid = () => {
    const rows = Array.from({ length: numCols }, () =>
        Array.from({ length: numRows }, () => Math.random() < 0.5)
    );
    return rows;
};

const possibleGrid = generatePossibleGrid();

interface QuestionData {
    word: string;
    image: string;
    row: number;
    col: number;
    direction: string;
}


const Crossword = () => {
    const [grid, setGrid] = useState<any[]>([]);
    const [answers, setAnswers] = useState<{ [key: string]: string }>({});
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleInputChange = (row: number, col: number, correctAnswer: string | number | readonly string[] | undefined, value: string) => {
        setAnswers({ ...answers, [`${row}-${col}`]: value.toUpperCase() });
        console.log(correctAnswer, value);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Function to place words properly
    const placeWordsInGrid = (words: { word: any; row: any; col: any; direction: any; }[]) => {
        const grid = generateEmptyGrid();

        words.forEach(({ word, row, col, direction }) => {
            for (let i = 0; i < word.length; i++) {
                if (direction === "across" && col + i < numRows) {
                    if (grid[row][col + i] === "" || grid[row][col + i] === word[i]) {
                        grid[row][col + i] = word[i]; // Keep same letters if overlapping
                    }
                } else if (direction === "down" && row + i < numCols) {
                    if (grid[row + i][col] === "" || grid[row + i][col] === word[i]) {
                        grid[row + i][col] = word[i]; // Keep same letters if overlapping
                    }
                }
            }
        });

        setGrid(grid)
    };

    const fetchQuestions = async () => {
        try {
            let data = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'AnimalCrossword.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/AnimalCrossword.json');
                data = module.default;
            }

            const shuffledData = shuffleArray(data);
            setTotalQuestion(shuffledData);
            setQuestions(shuffledData[currentIndex].words);
            placeWordsInGrid(shuffledData[currentIndex].words)
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };


    useEffect(() => {
        fetchQuestions();
        document.title = "Animal Crossword Puzzle for UKG Kids | General Awareness Worksheet"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        setShowImage(true);
        setTimeout(() => {
            if (currentIndex != totalQuestion.length - 1) {
                setCurrentIndex(currentIndex + 1);
                console.log(totalQuestion[currentIndex + 1].words)
                placeWordsInGrid(totalQuestion[currentIndex + 1].words)
                setQuestions(totalQuestion[currentIndex + 1].words)
            }
            else {
                setCurrentIndex(0);
                fetchQuestions();
            }
            setShowImage(false);
        }, 5000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Download a fun and educational UKG animal crossword worksheet. Help your child learn animal names, improve spelling, and boost memory with this interactive puzzle!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/animals/crossword" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord animalCrossword crossword-container" style={{ paddingTop: '0' }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Animal Crossword Puzzle</h1>

                                <p>
                                    Make animal learning exciting with this <strong>Animal Crossword Puzzle</strong> designed for <strong>UKG (Upper Kindergarten)</strong> students. This worksheet combines fun with education, helping young learners improve their vocabulary, spelling, and critical thinking skills while reinforcing their knowledge of animals.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify common animals and their names</li>
                                    <li>Improve spelling and vocabulary recognition</li>
                                    <li>Develop logical thinking and word-solving skills</li>
                                </ul>

                                <h2>📄 What&apos;s Inside the Worksheet?</h2>
                                <p>
                                    The crossword puzzle contains clues related to familiar animals like <em>lion</em>, <em>cat</em>, <em>dog</em>, <em>elephant</em>, and more. Children are asked to read the clues (with picture hints where applicable) and fill the correct animal names in the crossword boxes.
                                </p>

                                <h2>🧠 Skills Developed</h2>
                                <ul>
                                    <li>Language and literacy</li>
                                    <li>Animal identification</li>
                                    <li>Visual memory and word mapping</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>✨ Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Assist with the first few clues to help children get started</li>
                                    <li>Read aloud clues for auditory reinforcement</li>
                                    <li>Use animal toys or flashcards alongside the worksheet for multisensory learning</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Animal Crossword Puzzle</h1>

                            <p>
                                Make animal learning exciting with this <strong>Animal Crossword Puzzle</strong> designed for <strong>UKG (Upper Kindergarten)</strong> students. This worksheet combines fun with education, helping young learners improve their vocabulary, spelling, and critical thinking skills while reinforcing their knowledge of animals.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify common animals and their names</li>
                                <li>Improve spelling and vocabulary recognition</li>
                                <li>Develop logical thinking and word-solving skills</li>
                            </ul>

                            <h2>📄 What&apos;s Inside the Worksheet?</h2>
                            <p>
                                The crossword puzzle contains clues related to familiar animals like <em>lion</em>, <em>cat</em>, <em>dog</em>, <em>elephant</em>, and more. Children are asked to read the clues (with picture hints where applicable) and fill the correct animal names in the crossword boxes.
                            </p>

                            <h2>🧠 Skills Developed</h2>
                            <ul>
                                <li>Language and literacy</li>
                                <li>Animal identification</li>
                                <li>Visual memory and word mapping</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>✨ Tips for Parents and Teachers</h2>
                            <ul>
                                <li>Assist with the first few clues to help children get started</li>
                                <li>Read aloud clues for auditory reinforcement</li>
                                <li>Use animal toys or flashcards alongside the worksheet for multisensory learning</li>
                            </ul>
                        </article>
                        <div className="row">
                            <div className="col-12 col-lg-8 grid mb-5">
                                {possibleGrid && grid.map((rowArr, rowIndex) => (
                                    <div className="row1" key={rowIndex}>
                                        {rowArr.map((cell: string | number | readonly string[] | undefined, colIndex: number) => {
                                            const isCorrect = answers[`${rowIndex}-${colIndex}`] === cell;
                                            return (
                                                <input
                                                    key={`${rowIndex}-${colIndex}`}
                                                    className={`cell ${cell ? "filled" : "opacityZero"} ${answers[`${rowIndex}-${colIndex}`] && !isCorrect ? "wrong-answer" : ""}`}
                                                    maxLength={1}
                                                    value={possibleGrid[rowIndex][colIndex] ? cell : answers[`${rowIndex}-${colIndex}`] || ""}
                                                    onChange={(e) => handleInputChange(rowIndex, colIndex, cell, e.target.value)}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                            <div className="col-12 col-lg-4">
                                <h3 style={{ textAlign: 'left' }}>Clues:</h3>
                                {/* Clues Section with Images */}
                                <div className="clues">
                                    {questions.map((word, index) => (
                                        <div className="mt-4" key={index}>
                                            <img src={process.env.REACT_APP_API_URL + word.image} alt={word.word} className="clue-image img-thumbnail" />{" "}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 text-center">
                            <div className="background-blue" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next Question</div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            className="img-fluid"
                            src={process.env.REACT_APP_LOADING_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default Crossword;
